import React from 'react';
import './FmContentPrimitives.css';

const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

export type FmListRowTone = 'default' | 'warning' | 'critical';

export interface FmListRowProps {
  leading?: React.ReactNode;
  title: React.ReactNode;
  meta?: React.ReactNode;
  status?: React.ReactNode;
  actions?: React.ReactNode;
  selected?: boolean;
  selectable?: boolean;
  onSelect?: () => void;
  onClick?: () => void;
  tone?: FmListRowTone;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'data-testid'?: string;
  /** Arbitrary `data-*` hooks forwarded to the row element (e.g. `data-plan-id`). */
  [key: `data-${string}`]: string | number | boolean | undefined;
}

export interface FmListRowGroupProps {
  children: React.ReactNode;
  /**
   * When true, the group renders role="listbox" with role="option" children.
   * When false (default), the group is a plain container without listbox semantics.
   * Only set to true if the group contains selectable FmListRow instances.
   */
  selectable?: boolean;
  /**
   * @internal DS-internal className prop; forbidden in route-owned code.
   */
  className?: string;
  'data-testid'?: string;
}

const FmListRowGroup: React.FC<FmListRowGroupProps> = ({
  children,
  selectable = false,
  className,
  'data-testid': dataTestId,
}) => {
  const groupRole = selectable ? 'listbox' : undefined;
  return (
    <div className={cx('fm-list-row-group', className)} role={groupRole} data-testid={dataTestId}>
      {children}
    </div>
  );
};

interface FmListRowComponent extends React.FC<FmListRowProps> {
  Group: React.FC<FmListRowGroupProps>;
}

const FmListRowContent: React.FC<{ leading?: React.ReactNode; title: React.ReactNode; meta?: React.ReactNode; status?: React.ReactNode }> = ({
  leading,
  title,
  meta,
  status,
}) => (
  <>
    {leading ? <span className="fm-list-row__leading">{leading}</span> : null}
    <span className="fm-list-row__body">
      <span className="fm-list-row__title">{title}</span>
      {meta ? <span className="fm-list-row__meta">{meta}</span> : null}
    </span>
    {status ? <span className="fm-list-row__status">{status}</span> : null}
  </>
);

export const FmListRow: FmListRowComponent = ({
  leading,
  title,
  meta,
  status,
  actions,
  selected = false,
  selectable = false,
  onSelect,
  onClick,
  tone = 'default',
  className,
  'data-testid': dataTestId,
  ...rest
}) => {
  const interactive = Boolean(onSelect || onClick || selectable);
  const hasActions = Boolean(actions);

  const handleActivate = () => {
    onSelect?.();
    onClick?.();
  };

  const handleClick = () => {
    handleActivate();
  };

  const dataAttrs = Object.fromEntries(
    Object.entries(rest).filter(([key]) => key.startsWith('data-')),
  );

  const commonRowProps = {
    ...dataAttrs,
    className: cx(
      'fm-list-row',
      interactive && 'fm-list-row--interactive',
      selected && 'fm-list-row--selected',
      tone !== 'default' && `fm-list-row--${tone}`,
      className,
    ),
    'data-testid': dataTestId,
    ...(selectable ? { role: 'option', 'aria-selected': selected } : {}),
  };

  // Non-interactive row: just render content in grid
  if (!interactive) {
    return (
      <div {...commonRowProps}>
        <FmListRowContent leading={leading} title={title} meta={meta} status={status} />
        {actions ? <span className="fm-list-row__actions">{actions}</span> : null}
      </div>
    );
  }

  // Interactive row: outer grid wrapper (non-interactive) + button control (sibling) + actions (sibling)
  return (
    <div {...commonRowProps}>
      <button
        type="button"
        className={cx(
          'fm-list-row__control',
          hasActions && 'fm-list-row__control--with-actions',
        )}
        onClick={handleClick}
      >
        <FmListRowContent leading={leading} title={title} meta={meta} status={status} />
      </button>
      {actions ? <span className="fm-list-row__actions">{actions}</span> : null}
    </div>
  );
};

FmListRow.Group = FmListRowGroup;
